/**
 * Renderer Process Logger
 * 
 * レンダラープロセス用のロガー。
 * ログをメインプロセスに送信して、統一されたログファイルに記録する。
 */

export const LogLevel = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
} as const;

export type LogLevelType = 0 | 1 | 2 | 3;

interface LogMessage {
  level: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
  message: string;
  error?: Error;
  metadata?: Record<string, unknown>;
  source: string;
}

class RendererLogger {
  private logLevel: LogLevelType;

  constructor(logLevel: LogLevelType = LogLevel.INFO) {
    // 呼び出し側がログレベルを決定して渡す
    // 環境検出やlocalStorage操作は呼び出し側の責務
    this.logLevel = logLevel;
  }

  setLogLevel(level: LogLevelType): void {
    this.logLevel = level;
  }

  getLogLevel(): LogLevelType {
    return this.logLevel;
  }

  private getCallerInfo(): string {
    // スタックトレースから呼び出し元を取得
    const error = new Error();
    const stack = error.stack?.split('\n') || [];
    
    // Logger自身の呼び出しをスキップ
    for (let i = 3; i < stack.length; i++) {
      const line = stack[i];
      const match = line.match(/at\s+(?:.*\s+\()?(.+):(\d+):\d+\)?$/);
      if (match && !match[1].includes('logger.ts')) {
        const filename = match[1].split('/').pop() || 'unknown';
        return `${filename}:${match[2]}`;
      }
    }
    
    return 'unknown';
  }

  private shouldLog(level: LogLevelType): boolean {
    return level <= this.logLevel;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseArgs(args: any[]): { message: string; error?: Error; metadata?: Record<string, unknown> } {
    if (args.length === 0) {
      return { message: '' };
    }

    // 引数を文字列に変換してメッセージを構築
    const messageParts: string[] = [];
    let error: Error | undefined;
    const metadata: Record<string, unknown> = { /* no-op */ };
    let metadataIndex = 0;

    for (const arg of args) {
      if (arg instanceof Error) {
        // エラーオブジェクトは特別扱い（最初のエラーのみ保持）
        if (!error) {
          error = arg;
        }
        messageParts.push(arg.message);
      } else if (arg === null) {
        messageParts.push('null');
      } else if (arg === undefined) {
        messageParts.push('undefined');
      } else if (typeof arg === 'object') {
        // オブジェクトはJSON形式で表示
        try {
          messageParts.push(JSON.stringify(arg));
          // メタデータとしても保存
          metadata[`arg${metadataIndex++}`] = arg;
        } catch {
          // 循環参照などでJSON.stringifyが失敗した場合
          messageParts.push('[object Object]');
        }
      } else {
        // プリミティブ値はそのまま文字列化
        messageParts.push(String(arg));
      }
    }

    return {
      message: messageParts.join(' '),
      error,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined
    };
  }

  private sendToMain(logMessage: LogMessage): void {
    // Web版ではコンソールに出力
    // TODO: 将来的にはサーバーへのログ送信やIndexedDBへの保存を検討
    this.logToConsole(logMessage);
  }

  private logToConsole(logMessage: LogMessage): void {
    const method = logMessage.level.toLowerCase() as 'error' | 'warn' | 'info' | 'debug';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const args: any[] = [logMessage.message];
    if (logMessage.error) {
      args.push(logMessage.error);
    }
    if (logMessage.metadata) {
      // メタデータの各値を引数として追加
      Object.values(logMessage.metadata).forEach(value => args.push(value));
    }

    if (method !== 'debug') {
      console[method](`[${logMessage.level}]`, ...args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(...args: any[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const { message, error, metadata } = this.parseArgs(args);
    const logMessage: LogMessage = {
      level: 'ERROR',
      message,
      error,
      metadata,
      source: this.getCallerInfo()
    };

    this.sendToMain(logMessage);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn(...args: any[]): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const { message, metadata } = this.parseArgs(args);
    const logMessage: LogMessage = {
      level: 'WARN',
      message,
      metadata,
      source: this.getCallerInfo()
    };

    this.sendToMain(logMessage);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info(...args: any[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const { message, metadata } = this.parseArgs(args);
    const logMessage: LogMessage = {
      level: 'INFO',
      message,
      metadata,
      source: this.getCallerInfo()
    };

    this.sendToMain(logMessage);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug(...args: any[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const { message, metadata } = this.parseArgs(args);
    const logMessage: LogMessage = {
      level: 'DEBUG',
      message,
      metadata,
      source: this.getCallerInfo()
    };

    this.sendToMain(logMessage);
  }

  assert(condition: boolean, message?: string, metadata?: Record<string, unknown>): void {
    if (!condition) {
      const error = new Error(message || 'Assertion failed');
      
      // スタックトレースを取得
      const stack = error.stack || '';
      
      // エラーメッセージとスタックトレース全体をログに記録
      const logMessage: LogMessage = {
        level: 'ERROR',
        message: `ASSERTION FAILED: ${message || 'No message provided'}`,
        error,
        metadata: {
          assertionMessage: message || 'No message provided',
          fullStackTrace: stack,
          ...metadata  // 追加のメタデータをマージ
        },
        source: this.getCallerInfo()
      };

      this.sendToMain(logMessage);

      // Errorをスロー
      throw error;
    }
  }
}

/**
 * localStorageにログレベルを保存
 * Workerコンテキストでは何もしない
 */
export function saveLogLevelToStorage(level: LogLevelType): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    const levelName = ['ERROR', 'WARN', 'INFO', 'DEBUG'][level];
    localStorage.setItem('LOG_LEVEL', levelName);
  } catch {
    // 保存失敗は無視
  }
}

/**
 * 初期ログレベルを決定する
 * - Workerコンテキスト: INFO
 * - メインスレッド: localStorageまたは環境に基づく
 */
function getInitialLogLevel(): LogLevelType {
  // Workerコンテキストの場合はINFO固定
  if (typeof window === 'undefined') {
    return LogLevel.INFO;
  }

  // localStorageから読み込み
  try {
    const stored = localStorage.getItem('LOG_LEVEL');
    if (stored) {
      switch (stored.toUpperCase()) {
        case 'ERROR': return LogLevel.ERROR;
        case 'WARN': return LogLevel.WARN;
        case 'INFO': return LogLevel.INFO;
        case 'DEBUG': return LogLevel.DEBUG;
      }
    }
  } catch {
    // localStorage読み込み失敗は無視
  }

  // 開発環境かどうかで判定
  const isDevelopment = window.location?.hostname === 'localhost' ||
                        window.location?.hostname === '127.0.0.1';
  return isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
}

// シングルトンインスタンス
let rendererLogger: RendererLogger | null = null;

export function getRendererLogger(): RendererLogger {
  if (!rendererLogger) {
    rendererLogger = new RendererLogger(getInitialLogLevel());
  }
  return rendererLogger;
}

// テスト用のリセット関数
export function resetRendererLogger(): void {
  rendererLogger = null;
}

// デフォルトエクスポート - シングルトンインスタンスを作成
const defaultLogger = getRendererLogger();
export default defaultLogger;